<template>
    <nav class="top-bar" :class="{ 'is-hidden': !isVisible }">
        <div class="top-bar__branding">
            <img src="/abb-logo.svg" alt="ABB" class="top-bar__logo" />
            <div class="top-bar__divider"></div>
            <div class="top-bar__product">
                <div class="top-bar__product-name">U3t</div>
                <div class="top-bar__product-spec">{{ appName }}</div>
            </div>
        </div>

        <button
            class="top-bar__hamburger"
            :class="{ 'is-active': mobileMenuOpen }"
            @click="toggleMobileMenu"
            aria-label="Toggle menu"
        >
            <span></span>
            <span></span>
            <span></span>
        </button>

        <ul class="top-bar__nav" :class="{ 'is-open': mobileMenuOpen }">
            <li
                v-for="item in navigationItems"
                :key="item.id"
                class="top-bar__nav-item"
                :class="{
                    'has-dropdown': item.children && item.children.length > 0,
                    'is-expanded': expandedItems.has(item.id),
                }"
            >
                <a
                    :href="item.url || '#'"
                    class="top-bar__nav-link"
                    @click.prevent="
                        item.children && item.children.length > 0 ? toggleDropdown(item.id) : handleNavClick(item)
                    "
                >
                    <font-awesome-icon v-if="item.icon" :icon="parseIcon(item.icon)" class="nav-icon" />
                    {{ item.title }}
                    <font-awesome-icon
                        v-if="item.children && item.children.length > 0"
                        :icon="['fas', 'chevron-down']"
                        class="nav-chevron"
                        :class="{ 'is-rotated': expandedItems.has(item.id) }"
                    />
                </a>

                <ul v-if="item.children && item.children.length > 0" class="top-bar__dropdown">
                    <li v-for="child in item.children" :key="child.id" class="top-bar__dropdown-item">
                        <a
                            :href="child.url || '#'"
                            class="top-bar__dropdown-link"
                            @click.prevent="handleNavClick(child)"
                        >
                            <font-awesome-icon v-if="child.icon" :icon="parseIcon(child.icon)" class="dropdown-icon" />
                            {{ child.title }}
                        </a>
                    </li>
                </ul>
            </li>
        </ul>

        <div class="top-bar__controls">
            <button
                class="top-bar__control-btn"
                @click="toggleTheme"
                :aria-label="currentTheme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'"
            >
                <font-awesome-icon :icon="['fas', 'adjust']" />
            </button>
            <button class="top-bar__control-btn" @click="showInfoModal = true" aria-label="Description">
                <font-awesome-icon :icon="['fas', 'circle-info']" />
            </button>
            <button v-if="requestHelpLink" class="top-bar__control-btn" @click="openHelpLink" aria-label="Request help">
                <font-awesome-icon :icon="['fas', 'circle-question']" />
            </button>
            <button class="top-bar__control-btn top-bar__user" aria-label="User menu">
                <img
                    v-if="!azureStore.getPhotoLoader()"
                    :src="createBlobUrl(azureStore.getPhoto())"
                    alt="User Photo"
                    class="person-photo person-photo--rounded"
                />
                <font-awesome-icon v-else :icon="['fas', 'user']" />
            </button>
        </div>

        <div v-if="mobileMenuOpen" class="top-bar__overlay" @click="toggleMobileMenu"></div>

        <DescriptionModal
            v-model:visible="showInfoModal"
            title="U3t - Team Story description"
            :description="azureStore.getPageDescription() || ''"
            :editDescriptionLink="editDescriptionLink"
            :showEdit="azureStore.isUserOwner()"
        />
    </nav>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, computed } from "vue";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import type { MenuItem } from "@/types/navigation";
import { useTheme } from "@/composables/useTheme";
import DescriptionModal from "@/components/DescriptionModal.vue";
import { useAzureStore } from "@/stores/azure";
import { useAuth } from "@/composables/useAuth";

const { navigationItems, appName, descriptionId } = defineProps<{
    navigationItems: MenuItem[];
    appName: string;
    descriptionId: number;
}>();

//const homePageLink = 'https://abb.sharepoint.com/sites/NO-CompetenceLibrary/SitePages/TeamView.aspx'
const requestHelpLink =
    "https://abb.sharepoint.com/sites/NO-CompetenceLibrary/Lists/BUOGCSupport/AllItems.aspx?Application=U3t";

const editDescriptionLink = computed(() => {
    if (descriptionId) {
        return `https://abb.sharepoint.com/sites/NO-CompetenceLibrary/PA-OGC-KPI/Lists/PageDescriptions/EditForm.aspx?ID=${descriptionId}`;
    }
    return undefined;
});

const azureStore = useAzureStore();

const { isAuthenticated } = useAuth();

const mobileMenuOpen = ref(false);
const showInfoModal = ref(false);
const expandedItems = ref<Set<string>>(new Set());
const { currentTheme, toggleTheme, initializeTheme } = useTheme();
const isVisible = ref(true);
const lastScrollY = ref(0);
const scrollThreshold = 10;
const mobileBreakpoint = 1585;

const stopWatcher = watch(
    isAuthenticated,
    async (isAuth) => {
        if (isAuth) {
            console.warn("TopBar watcher", isAuth);
            stopWatcher();
            const tasks = [
                azureStore.loadMyPhoto(),
                azureStore.loadIsUserOwner(),
                azureStore.loadPageDescription(descriptionId),
            ];
            await Promise.all(tasks);
        }
    },
    { immediate: true }
);

const createBlobUrl = (photo: Blob | null) => {
    if (photo && photo instanceof Blob) {
        return URL.createObjectURL(photo);
    }
    return undefined;
};

// Parse FontAwesome icon class string (e.g., "fa fa-chart-area")
const parseIcon = (iconClass: string): string[] => {
    const iconName = iconClass.split(" ").pop() || "";
    return ["fas", iconName.replace("fa-", "")];
};

const toggleMobileMenu = () => {
    mobileMenuOpen.value = !mobileMenuOpen.value;
    // Prevent body scroll when menu is open
    if (mobileMenuOpen.value) {
        document.body.style.overflow = "hidden";
    } else {
        document.body.style.overflow = "";
        expandedItems.value.clear();
    }
};

const toggleDropdown = (itemId: string) => {
    if (expandedItems.value.has(itemId)) {
        expandedItems.value.delete(itemId);
    } else {
        if (window.innerWidth < mobileBreakpoint) {
            expandedItems.value.clear();
        }
        expandedItems.value.add(itemId);
    }
};

const navigateTo = (url: string) => {
    window.location.href = url;
    mobileMenuOpen.value = false;
    document.body.style.overflow = "";
};

const handleNavClick = (item: MenuItem) => {
    if (item.url) {
        navigateTo(item.url);
    }
};

const openHelpLink = () => {
    if (requestHelpLink) {
        window.open(requestHelpLink, "_blank", "noopener,noreferrer");
    }
};

const handleScroll = () => {
    const currentScrollY = window.scrollY;

    // Always show at top of page
    if (currentScrollY < scrollThreshold) {
        isVisible.value = true;
        lastScrollY.value = currentScrollY;
        return;
    }

    // Show when scrolling up, hide when scrolling down
    if (currentScrollY < lastScrollY.value) {
        // Scrolling up
        isVisible.value = true;
    } else if (currentScrollY > lastScrollY.value + scrollThreshold) {
        // Scrolling down (with threshold to avoid jitter)
        isVisible.value = false;
    }

    lastScrollY.value = currentScrollY;
};

onMounted(() => {
    initializeTheme();
    window.addEventListener("scroll", handleScroll, { passive: true });
});

onUnmounted(() => {
    window.removeEventListener("scroll", handleScroll);
});
</script>

<style scoped lang="scss">
$breakpoint-mobile: 1585px;

.top-bar {
    display: flex;
    align-items: center;
    background-color: var(--bg-stateless-base);
    border-bottom: 1px solid var(--bg-stateless-offset);
    padding: 0 1.5rem;
    height: 64px;
    gap: 2rem;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1000;
    transform: translateY(0);
    transition: transform 0.3s ease;

    &.is-hidden {
        transform: translateY(-100%);
    }

    &__branding {
        display: flex;
        align-items: center;
        gap: 1rem;
    }

    &__logo {
        height: 32px;
        width: auto;
    }

    &__divider {
        width: 1px;
        height: 32px;
        background-color: var(--bg-stateless-offset);
    }

    &__product {
        display: flex;
        flex-direction: column;
        line-height: 1.2;
    }

    &__product-name {
        font-weight: 600;
        font-size: 0.875rem;
        color: var(--color-text-primary);
    }

    &__product-spec {
        font-size: 0.75rem;
        color: var(--color-text-secondary);
    }

    &__hamburger {
        display: none;
        flex-direction: column;
        justify-content: space-around;
        width: 32px;
        height: 32px;
        background: transparent;
        border: none;
        cursor: pointer;
        padding: 4px;
        z-index: 1001;

        span {
            width: 100%;
            height: 2px;
            background-color: var(--color-text-primary);
            border-radius: 2px;
            transition: all 0.3s ease;
            transform-origin: center;
        }

        &.is-active {
            span:nth-child(1) {
                transform: translateY(9px) rotate(45deg);
            }
            span:nth-child(2) {
                opacity: 0;
            }
            span:nth-child(3) {
                transform: translateY(-9px) rotate(-45deg);
            }
        }
    }

    &__overlay {
        display: none;
    }

    &__nav {
        display: flex;
        list-style: none;
        margin: 0;
        padding: 0;
        gap: 0.5rem;
        flex: 1;
    }

    &__nav-item {
        position: relative;

        &.is-expanded > .top-bar__dropdown {
            display: block;
            opacity: 1;
            transform: translateY(0);
            pointer-events: auto;
        }

        // Enable hover on desktop/wider screens
        @media (min-width: $breakpoint-mobile) {
            &:hover > .top-bar__dropdown {
                display: block;
                opacity: 1;
                transform: translateY(0);
                pointer-events: auto;
            }

            &:hover .nav-chevron {
                transform: rotate(180deg);
            }
        }
    }

    &__nav-link {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.75rem 1rem;
        text-decoration: none;
        color: var(--color-text-primary);
        font-size: 0.875rem;
        font-weight: 500;
        border-radius: 4px;
        transition: background-color 0.2s ease;
        white-space: nowrap;

        &:hover {
            background-color: var(--bg-secondary-hover);
        }

        .nav-icon {
            font-size: 1rem;
            color: var(--color-text-secondary);
        }

        .nav-chevron {
            font-size: 0.75rem;
            margin-left: 0.25rem;
            color: var(--color-text-secondary);
            display: none;
            transition: transform 0.2s ease;

            &.is-rotated {
                transform: rotate(180deg);
            }
        }
    }

    &__dropdown {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        width: 100%;
        min-width: 220px;
        max-height: calc(100dvh - 80px);
        overflow-y: auto;
        background-color: var(--bg-stateless-base);
        border: solid var(--bg-stateless-offset);
        border-top-width: 0px;
        border-left-width: 1px;
        border-right-width: 1px;
        border-bottom-width: 1px;
        border-radius: 0px 0px 4px 4px;
        box-shadow:
            0 4px 6px rgba(0, 0, 0, 0.07),
            0 2px 4px rgba(0, 0, 0, 0.05);
        list-style: none;
        margin: 0 0 0 0;
        padding: 0.5rem 0;
        z-index: 1000;
        display: none;
        opacity: 0;
        transform: translateY(-10px);
        transition:
            opacity 0.2s ease,
            transform 0.2s ease;
        pointer-events: none;
    }

    &__dropdown-item {
        margin: 0;
        width: 100%;
    }

    &__dropdown-link {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        padding: 0.75rem 1.25rem;
        width: 100%;
        text-decoration: none;
        color: var(--color-text-primary);
        font-size: 0.875rem;
        transition: background-color 0.2s ease;

        &:hover {
            background-color: var(--bg-secondary-hover);
        }

        .dropdown-icon {
            font-size: 0.875rem;
            color: var(--color-text-secondary);
            width: 1rem;
            text-align: center;
        }
    }

    &__controls {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        margin-left: auto;
    }

    &__description {
        flex: 1;
        text-align: center;
        color: var(--color-text-primary);
        font-size: 0.875rem;
        font-weight: 500;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        padding: 0 1rem;
    }

    &__control-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 40px;
        height: 40px;
        border: none;
        background: transparent;
        border-radius: 50%;
        cursor: pointer;
        color: var(--color-text-primary);
        transition: background-color 0.2s ease;
        font-size: 1.125rem;

        &:hover {
            background-color: var(--bg-secondary-hover);
        }

        &:active {
            background-color: var(--bg-secondary-active);
        }
    }

    &__user {
        background-color: var(--bg-stateless-alternative);
    }
    .person-photo {
        width: 30px;
        height: 30px;
        object-fit: cover;

        &--rounded {
            border-radius: 50%;
        }
    }
}

// Dark mode support
[data-theme="dark"] {
    .top-bar {
        border-bottom-color: var(--bg-stateless-subtle);
    }
}

// Responsive - Mobile Menu
@media (max-width: $breakpoint-mobile) {
    .top-bar {
        &__hamburger {
            display: flex;
            order: 3;
        }

        &__nav {
            order: 4;
        }

        &__controls {
            order: 2;
            margin-left: auto;
        }

        &__description {
            display: none;
        }

        &__overlay {
            display: block;
            position: absolute;
            top: 64px;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.5);
            z-index: 9999;
            backdrop-filter: blur(2px);
        }

        &__nav {
            position: absolute;
            top: 64px;
            right: 0;
            height: calc(100dvh - 71px);
            width: min(400px, 100vw);
            flex-direction: column;
            background-color: var(--bg-stateless-base);
            padding: 1rem 0;
            gap: 0;
            box-shadow: -2px 0 8px rgba(0, 0, 0, 0.1);
            transform: translateX(100%);
            transition: transform 0.3s ease;
            overflow-y: auto;
            z-index: 10000;

            &.is-open {
                transform: translateX(0);
            }
        }

        &__nav-item {
            width: 100%;

            // Disable hover effects on mobile
            &:hover > .top-bar__dropdown {
                display: none;
            }

            &:hover .nav-chevron {
                transform: none;
            }

            &.has-dropdown {
                flex-direction: column;
            }

            &.is-expanded > .top-bar__dropdown {
                display: block !important;
            }

            &.is-expanded .nav-chevron {
                transform: rotate(180deg);
            }
        }

        &__nav-link {
            width: 100%;
            padding: 1rem 1.5rem;
            border-radius: 0;
            justify-content: space-between;

            .nav-chevron {
                display: inline-block;
            }

            &:hover {
                background-color: var(--bg-secondary-hover);
            }
        }
    }
}

// Extra small devices
@media (max-width: 480px) {
    .top-bar {
        padding: 0 1rem;

        &__product {
            display: none;
        }

        &__divider {
            display: none;
        }

        &__nav {
            width: 100vw;
        }

        &__dropdown {
            position: static;
            display: block !important;
            opacity: 1;
            transform: none;
            pointer-events: auto;
            box-shadow: none;
            border: none;
            border-radius: 0;
            background-color: var(--bg-stateless-alternative);
            padding: 0;
            margin: 0;
            max-height: none;
            overflow-y: visible;
        }

        &__dropdown-link {
            padding: 0.875rem 1.5rem 0.875rem 3rem;
        }

        &__controls {
            margin-left: 0;
            margin-right: 0.5rem;
        }
    }
}
</style>
